import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { DataTable } from '@/components/DataTable';
import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { type SearchData, searchQueries } from '@/lib/queries/search';

import { DateRangeDisplay } from '../resource/-components/DateRangeDisplay';
import { SearchBar } from './-components/SearchBar';
import { getColumns } from './-components/columns';

export const Route = createFileRoute('/_root/(search)/')({
  component: RouteComponent,
  validateSearch: searchParams =>
    z
      .object({
        searchTerm: z.string().optional(),
        from: z.string().optional(),
        to: z.string().optional(),
        showDeleted: z.boolean().optional(),
      })
      .parse(searchParams),
  loaderDeps: ({ search: { searchTerm, from, to, showDeleted } }) => ({
    searchTerm,
    from,
    to,
    showDeleted,
  }),
  loader: async ({ context, deps: { searchTerm, from, to, showDeleted } }) => {
    const defaultProject = await preLoadDefaultProject({ context });

    context.queryClient.prefetchQuery(
      searchQueries.search({
        projectId: defaultProject.id,
        searchTerm,
        from,
        to,
        showDeleted,
      }),
    );

    return defaultProject;
  },
});

const ResultsTable = ({
  data,
  searchTerm,
  from,
  to,
  showDeleted,
}: {
  data: SearchData;
  searchTerm: string | undefined;
  from: string | undefined;
  to: string | undefined;
  showDeleted: boolean | undefined;
}) => {
  const colums = useMemo(
    () =>
      getColumns({
        searchTerm,
        from,
        to,
        showDeleted: showDeleted === true,
      }),
    [searchTerm, from, to, showDeleted],
  );

  return (
    <DataTable
      data={data.results}
      columns={colums}
      headersTranslationPath="search.table"
      optionsTranslationPaths={{
        deleted: 'search.table.deleted.options',
      }}
    />
  );
};

function RouteComponent() {
  const { searchTerm, from, to, showDeleted } = Route.useSearch();
  const defaultProject = Route.useLoaderData();

  const { data, isPending } = useQuery(
    searchQueries.search({
      projectId: defaultProject.id,
      searchTerm,
      from,
      to,
      showDeleted,
    }),
  );

  const { FeatureSearchCoverage } = useFeatureFlags();
  if (FeatureSearchCoverage !== true) {
    return <Navigate to="/integrations" />;
  }

  return (
    <>
      <div className="w-full flex items-center flex-col">
        <h1 className="lg:text-5xl md:text-3xl sm:text-xl text-zinc-950 mb-6 dark:text-white font-medium">
          <FormattedMessage id="search.title" />
        </h1>
        <div className="w-full flex items-center justify-center">
          <SearchBar
            isSearching={isPending}
            initialValues={{
              ...(searchTerm !== undefined ? { searchTerm } : undefined),
              ...(from !== undefined ? { from: new Date(from) } : undefined),
              ...(to !== undefined ? { to: new Date(to) } : undefined),
              ...(showDeleted !== undefined ? { showDeleted } : undefined),
            }}
          />
        </div>
        <div className="mt-8 w-full">
          {data !== undefined && data !== null && (
            <>
              <div className="w-fit">
                <DateRangeDisplay
                  from={from}
                  to={to}
                  prefix="Resources updated"
                />
              </div>
              <ResultsTable
                data={data}
                searchTerm={searchTerm}
                from={from}
                to={to}
                showDeleted={showDeleted}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
